// Core portfolio data types
export interface Property {
  id: string;
  address: string;
  purchasePrice: number;
  currentValue: number;
  purchaseDate: string;
  propertyType: 'House' | 'Apartment' | 'Townhouse' | 'Villa' | 'Storage' | 'Workspace/Office' | 'Land' | 'Farm/Rural' | 'Commercial' | 'Health Care' | 'Child Care' | 'Other';
  bedrooms: number;
  bathrooms: number;
  carSpaces: number;
  landSize: number; // sqm
  floorSize: number; // sqm
  imageUrl?: string;
  isOwnerOccupied?: boolean;
  ownershipEntity?: 'Individual' | 'Joint' | 'Trust' | 'Company' | 'SMSF';
  stampDuty?: number;
  stampDutyFormula?: string;
  propertyState?: 'NSW' | 'VIC' | 'QLD' | 'WA' | 'SA' | 'TAS' | 'ACT' | 'NT' | '';
  countryCode?: string;

  // Loan/Mortgage information
  loanAmount?: number; // Current outstanding loan balance
  offsetBalance?: number; // Balance in offset account reducing interest
  interestRate?: number; // Annual interest rate as percentage
  loanType?: 'principal-interest' | 'interest-only'; // Type of loan
  loanTermYears?: number; // Original loan term in years
  loanStartDate?: string; // When the loan was taken out
  interestOnlyExpiryDate?: string; // When the interest-only period ends

  // Thumbnail metadata to track source and status
  thumbnailMetadata?: {
    source: 'stored' | 'direct'; // stored in Firebase Storage vs direct Google API URL
    fallbackUsed: boolean; // whether fallback mechanism was used
    fetchedAt: string; // when the thumbnail was last fetched
  };

  // Historical data structure
  historicalData: {
    managementInfo: {
      [year: string]: ManagementInfo;
    };
  };

  // Year period configuration (financial year, calendar year, custom)
  yearPeriodConfig?: YearPeriodConfig;

  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;

  // Purchase-related documents
  purchaseDocuments?: DocumentMetadata[];

  // Important event dates for notification system
  rentReviewDate?: string; // When rent increase takes effect
  councilRatesDueDate?: string; // Council rates payment due date
  waterRatesDueDate?: string; // Water rates payment due date
  loanReviewDate?: string; // Bank/lender review or refinance date
  fireExemptionCertDate?: string; // Fire safety exemption certificate expiry
  poolSafetyCertDate?: string; // Pool safety certificate expiry
  bondLodgementDate?: string; // Bond lodgement date (for tenancy start)
  stratumAGMDate?: string; // Strata/body corporate AGM date
}

export interface InspectedProperty {
  id: string;
  address: string;
  countryCode?: string;
  propertyType: PropertyType;
  bedrooms: number;
  bathrooms: number;
  carSpaces: number;
  landSize: number; // sqm
  floorSize: number; // sqm
  agentPrice?: number;
  inspected: boolean;
  favourite: boolean;
  sold: boolean;
  notes?: string;
  agentContact?: {
    name?: string;
    phone?: string;
    email?: string;
    agency?: string;
  };
  reports: DocumentMetadata[];
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;

  // Financial fields for analysis
  expectedRent?: number;          // Weekly expected rent
  strataFees?: number;            // Quarterly strata/body corp fees
  councilRates?: number;          // Annual council rates
  waterRates?: number;            // Annual water rates
  insurance?: number;             // Annual insurance estimate
  estimatedYield?: number;        // Calculated gross yield percentage

  // Scenario Planner integration
  linkedScenarioId?: string;      // Link to Scenario Planner scenario

  // Optional Strategy Assessment (1-5 stars or rating)
  strategyAssessment?: {
    location: number;
    potentialYield: number;
    growthPotential: number;
    valueAdd: number;
    overall: number;
  };

  // AI Insights
  aiAnalysis?: string;            // Summary analysis from Gemini
  aiRecommendation?: string;      // Actionable advice (e.g., "Good Buy", "Watch", "Avoid")
}



export type PaymentFrequency = 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface DocumentMetadata {
  id: string;
  fileName: string;
  storageUrl: string;
  uploadDate: string;
  fileType: string; // MIME type
  fileSize: number; // bytes
  totalPages?: number;
  pagesAnalyzed?: number;
  analyzedData?: Partial<ManagementInfo>;
  analysisStatus: 'pending' | 'analyzing' | 'completed' | 'failed';
  confidenceScores?: {
    [key: string]: number; // field name -> confidence score (0-100)
  };
  scanQualityWarning?: boolean;
  errorMessage?: string;
}

export interface YearPeriodConfig {
  type: 'financial' | 'calendar' | 'custom';
  startMonth: number; // 1-12 (1=January, 7=July for FY)
  label?: string; // e.g., "FY 2024" or "CY 2024"
}

export interface ManagementInfo {
  weeklyRent: number;
  annualRent: number;
  rentFrequency?: PaymentFrequency;
  bodyCorpFees: number;
  bodyCorpFeesFrequency?: PaymentFrequency;
  councilRates: number;
  councilRatesFrequency?: PaymentFrequency;
  waterRates: number;
  waterRatesFrequency?: PaymentFrequency;
  insurance: number;
  insuranceFrequency?: PaymentFrequency;
  maintenance: number;
  maintenanceFrequency?: PaymentFrequency;
  managementFees: number;
  managementFeesFrequency?: PaymentFrequency;
  managementFeeType?: 'fixed' | 'percentage'; // Whether management fee is fixed amount or percentage of rent
  managementFeePercentage?: number; // Percentage value if managementFeeType is 'percentage'
  propertyTax: number; // Property/land tax expense
  propertyTaxFrequency?: PaymentFrequency;
  vacancyWeeks: number;
  otherExpenses: number;
  otherExpensesFrequency?: PaymentFrequency;
  vacancyNotes?: string;
  archived?: boolean;
  archivedAt?: string;

  // Custom expenses with descriptions
  customExpenses?: Array<{
    id?: string;
    description: string;
    amount: number;
    frequency: PaymentFrequency;
  }>;

  // Document uploads for this year
  documents?: DocumentMetadata[];

  // Important event dates (for notifications)
  rentIncreaseDate?: string; // When rent increase takes effect
  tenantContractEndDate?: string; // When tenant lease/contract ends
  councilRatesDueDate?: string; // Council rates payment due date
  waterRatesDueDate?: string; // Water rates payment due date
  insuranceDueDate?: string; // Insurance renewal date
  loanReviewDate?: string; // Bank loan review/refinance date
  fireExemptionCertDate?: string; // Fire safety certificate expiry
  poolSafetyCertDate?: string; // Pool safety certificate expiry
  bondLodgementDate?: string; // Bond lodgement date
  stratumAGMDate?: string; // Strata/Body Corp AGM date
  events?: Array<{
    date: string;
    title: string;
    description: string;
    category: 'rent' | 'expense' | 'maintenance' | 'other';
  }>;
}

export interface CustomAsset {
  id: string;
  name: string;
  value: number;
}

export interface OtherAssets {
  cash?: number;
  stocks?: number;
  crypto?: number;
  superannuation?: number;
  other?: number;
  customAssets?: CustomAsset[];
}

export interface Portfolio {
  id: string;
  userId: string;
  accountId?: string;
  properties: Property[];
  inspections?: InspectedProperty[];
  otherAssets: OtherAssets;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

// User and authentication types
export interface User {
  id?: string;
  uid: string;
  email: string;
  displayName?: string;
  role?: 'user' | 'admin' | 'superadmin' | 'read_only' | 'normal_user';
  isAdmin?: boolean;
  isAuthorized?: boolean;
  accountId?: string;
  photoURL?: string;
  countryCode?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthorizedUser {
  email: string;
  role: 'user' | 'admin' | 'superadmin' | 'read_only' | 'normal_user';
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
  updatedBy?: string;
  hasSeenWelcomeAlert?: boolean; // Flag to track if user has seen the welcome alert
  themePreference?: 'light' | 'dark' | 'system'; // User's theme preference
  countryCode?: string; // User's default country
  alertPreferences?: any; // User's alert notification preferences (stored as JSON)
}

export interface UserAccount {
  email: string;
  accountId: string;
  assignedAt: string;
  assignedBy: string;
}

export interface Account {
  id: string;
  name: string;
  type: 'investment' | 'personal' | 'business';
  description?: string;
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
  updatedBy?: string;
}

// Financial calculation types
export interface PortfolioMetrics {
  totalValue: number;
  totalInvested: number;
  totalEquity: number;
  totalDebt?: number;
  averageYield: number;
  totalAnnualRent: number;
  totalAnnualExpenses: number;
  annualCashFlow: number;
  capitalGrowth: number;
  capitalGrowthPercentage: number;
  propertiesCount: number;
  usableEquity?: number;
}

export interface PropertyMetrics {
  propertyId: string;
  address: string;
  currentValue: number;
  purchasePrice: number;
  equity: number;
  weeklyRent: number;
  annualRent: number;
  effectiveAnnualRent: number; // After vacancy
  annualExpenses: number;
  annualCashFlow: number;
  grossYield: number;
  netYield: number;
  capitalGrowth: number;
  capitalGrowthPercentage: number;
  vacancyWeeks: number;
  expenseRatio: number;
  usableEquity?: number;
}

export interface FinancialSummary {
  portfolioMetrics: PortfolioMetrics;
  propertyMetrics: PropertyMetrics[];
  diversificationScore: number;
  riskScore: number;
  recommendations: string[];
}

// AI advice types
export interface DailyRecommendation {
  userId: string;
  date: string;
  recommendationText: string;
  createdAt: string;
}

export interface AIAdviceRequest {
  query?: string;
  includePropertyDetails?: boolean;
  portfolioData: Portfolio;
  context?: string;
}

export interface AIAdviceResponse {
  advice: string;
  analysis: PortfolioAnalysis;
  timestamp: string;
  context?: string;
}

export interface PortfolioAnalysis {
  totalValue: number;
  totalInvested: number;
  averageYield: number;
  totalCashflow: number;
  diversificationScore: number;
  recommendations: string[];
  properties: PropertyAnalysis[];
}

export interface PropertyAnalysis {
  propertyId: string;
  address: string;
  currentYield: number;
  capitalGrowth: number;
  cashflow: number;
  recommendations: string[];
  riskFactors: string[];
}

// API response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp?: string;
}

export interface PaginatedResponse<T = any> extends ApiResponse<T[]> {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

// Form and UI types
export interface PropertyFormData extends Omit<Property, 'id' | 'createdAt' | 'updatedAt'> {
  id?: string;
}

export interface ManagementInfoFormData extends ManagementInfo {
  year: string;
}

export interface FilterOptions {
  propertyType?: string[];
  priceRange?: [number, number];
  yieldRange?: [number, number];
  location?: string[];
  sortBy?: 'address' | 'value' | 'yield' | 'cashflow' | 'growth';
  sortOrder?: 'asc' | 'desc';
}

// Chart and visualization types
export interface ChartDataPoint {
  x: string | number;
  y: number;
  label?: string;
  color?: string;
}

export interface TimeSeriesData {
  date: string;
  value: number;
  property?: string;
  metric?: string;
}

export interface PortfolioSnapshot {
  date: string;
  totalValue: number;
  totalInvested: number;
  totalCashFlow: number;
  averageYield: number;
  propertiesCount: number;
}

// Property Notes & Timeline types
export const NOTE_CATEGORIES = [
  'general',
  'maintenance',
  'tenant',
  'financial',
  'legal',
  'renovation',
] as const;

export type NoteCategory = typeof NOTE_CATEGORIES[number];

export interface PropertyNote {
  id: string;
  propertyId: string;
  userId: string;
  accountId: string;

  // Note details
  category: NoteCategory;
  title: string;
  content: string;

  // Event date (when the event occurred, not when the note was created)
  date: string;

  // Financial information (for maintenance, renovation notes)
  amount?: number;

  // Tags for organization
  tags?: string[];

  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

// API Request/Response Types for Property Notes
export interface CreateNoteRequest {
  propertyId: string;
  category: NoteCategory;
  title: string;
  content: string;
  date: string;
  amount?: number;
  tags?: string[];
}

export interface UpdateNoteRequest {
  category?: NoteCategory;
  title?: string;
  content?: string;
  date?: string;
  amount?: number;
  tags?: string[];
}

export interface GetNotesRequest {
  propertyId: string;
  category?: NoteCategory;
  startDate?: string;
  endDate?: string;
  tags?: string[];
  sortBy?: 'date' | 'createdAt' | 'amount';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

export interface NotesStats {
  totalNotes: number;
  totalMaintenanceCost: number;
  lastMaintenanceDate?: string;
  tenantChangesCount: number;
  notesByCategory: Record<NoteCategory, number>;
}

// Error types
export interface AppError {
  code: string;
  message: string;
  details?: any;
  timestamp: string;
}

export interface ValidationError extends AppError {
  field: string;
  value: any;
}

// Constants


export const USER_ROLES = [
  'user',
  'admin',
  'superadmin'
] as const;

export const ACCOUNT_TYPES = [
  'investment',
  'personal',
  'business'
] as const;

export const PROPERTY_TYPES = [
  'House',
  'Apartment',
  'Unit',
  'Townhouse',
  'Villa',
  'Land',
  'Commercial'
] as const;

export type PropertyType = typeof PROPERTY_TYPES[number];
// Missing files temporarily commented out to fix build
export * from './alerts';
export * from './reminders';
export * from './inbound-email';

export type OwnershipEntity = 'Individual' | 'Trust' | 'Company' | 'SMSF';
export const OWNERSHIP_ENTITIES: OwnershipEntity[] = ['Individual', 'Trust', 'Company', 'SMSF'];

export type UserRole = typeof USER_ROLES[number];
export type AccountType = typeof ACCOUNT_TYPES[number];

// ============================================================================
// INBOUND EMAIL TYPES
// ============================================================================
export interface EmailAttachment {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  storageUrl: string;
  analysisStatus?: 'pending' | 'analyzing' | 'completed' | 'failed';
}

export interface PropertyEmail {
  id: string;
  propertyId: string;
  accountId: string;
  year: string;
  subject: string;
  senderEmail: string;
  senderName?: string;
  forwarderEmail: string;
  receivedAt: string;
  processedAt: string;
  htmlBody?: string;
  textBody?: string;
  summary?: string; // AI Summary
  attachments: EmailAttachment[];
  status: 'pending' | 'processed' | 'failed' | 'rejected';
  rejectionReason?: 'unregistered_sender' | 'unknown_property' | 'invalid_format';
  analysisStatus?: 'pending' | 'analyzing' | 'completed' | 'failed';
  analyzedData?: any;
  confidenceScores?: any;
  originalEmailUrl?: string;
  authStatus?: {
    dkim?: string;
    spf?: string;
    dmarc?: string;
    isAuthenticated?: boolean;
  };
  createdAt: string;
  updatedAt?: string;
}

// ============================================================================
// FINANCIAL LEDGER TYPES
// ============================================================================

// Transaction Types
export interface Transaction {
  id: string;
  accountId: string; // Links to Account
  userId: string;

  // Source information
  source: 'bank' | 'manual' | 'import'; // Where the transaction came from
  bankConnectionId?: string; // If from bank, which connection
  externalId?: string; // Bank's transaction ID (for deduplication)

  // Transaction details
  date: string; // Transaction date (ISO format)
  postedDate?: string; // When it posted to account
  amount: number; // Positive for income, negative for expenses
  merchantName?: string;
  description: string;

  // Categorization
  category: TransactionCategory;
  subcategory?: string;
  propertyId?: string; // Which property this relates to
  isPropertyRelated: boolean;

  // Auto-categorization metadata
  categorizedBy: 'user' | 'rule' | 'ai' | 'default';
  categorizationConfidence?: number; // 0-1 for AI categorization
  suggestedCategories?: Array<{
    category: TransactionCategory;
    subcategory?: string;
    confidence: number;
  }>;

  // Reconciliation
  reconciliationStatus: 'pending' | 'reviewed' | 'approved' | 'disputed' | 'excluded';
  reviewedBy?: string;
  reviewedAt?: string;
  notes?: string;

  // Linked entities
  linkedTransactionIds?: string[]; // For splits or transfers
  attachments?: Array<{
    id: string;
    name: string;
    url: string;
    type: string;
    uploadedAt: string;
  }>;

  // Tax information
  isTaxDeductible: boolean;
  taxCategory?: 'rental_income' | 'rental_expense' | 'capital_improvement' | 'depreciation' | 'other';

  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

// Transaction Categories
export const TRANSACTION_CATEGORIES = [
  // Income categories
  'rental_income',
  'security_deposit',
  'late_fees',
  'other_income',

  // Property expense categories
  'mortgage_payment',
  'property_insurance',
  'property_tax',
  'hoa_fees',
  'utilities',
  'repairs_maintenance',
  'property_management',
  'landscaping',
  'pest_control',
  'cleaning',
  'legal_professional',
  'advertising_marketing',
  'supplies',
  'capital_improvements',

  // Other categories
  'transfer',
  'personal',
  'uncategorized',
] as const;

export type TransactionCategory = typeof TRANSACTION_CATEGORIES[number];

// Categorization Rules
export interface CategorizationRule {
  id: string;
  accountId: string;
  userId: string;

  // Rule definition
  name: string;
  description?: string;
  enabled: boolean;
  priority: number; // Higher priority rules run first

  // Matching criteria (all must match for rule to apply)
  conditions: {
    merchantNameContains?: string[];
    descriptionContains?: string[];
    amountEquals?: number;
    amountGreaterThan?: number;
    amountLessThan?: number;
    dateAfter?: string;
    dateBefore?: string;
    bankConnectionId?: string;
  };

  // Actions to apply when rule matches
  actions: {
    category: TransactionCategory;
    subcategory?: string;
    propertyId?: string;
    isPropertyRelated?: boolean;
    isTaxDeductible?: boolean;
    taxCategory?: string;
    notes?: string;
  };

  // Rule statistics
  timesApplied: number;
  lastApplied?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

// Ledger Summary (aggregated view)
export interface LedgerSummary {
  accountId: string;

  // Date range
  startDate: string;
  endDate: string;

  // Summary by category
  incomeByCategory: Record<TransactionCategory, number>;
  expensesByCategory: Record<TransactionCategory, number>;

  // Summary by property
  incomeByProperty: Record<string, number>; // propertyId -> amount
  expensesByProperty: Record<string, number>;
  cashFlowByProperty: Record<string, number>;

  // Totals
  totalIncome: number;
  totalExpenses: number;
  netCashFlow: number;

  // Reconciliation status
  transactionsCount: number;
  reviewedCount: number;
  pendingCount: number;

  // Period comparisons
  previousPeriodComparison?: {
    incomeChange: number;
    expensesChange: number;
    cashFlowChange: number;
    incomeChangePercent: number;
    expensesChangePercent: number;
    cashFlowChangePercent: number;
  };

  // Metadata
  generatedAt: string;
  generatedBy: string;
}

// Tax Report Types
export interface TaxReport {
  id: string;
  accountId: string;
  userId: string;

  // Report details
  taxYear: number;
  reportType: 'schedule_e' | 'capital_gains' | 'full_report';

  // Property-specific breakdown
  properties: Array<{
    propertyId: string;
    address: string;

    // Income
    rentalIncome: number;
    otherIncome: number;
    totalIncome: number;

    // Expenses
    advertising: number;
    auto: number;
    cleaning: number;
    commissions: number;
    insurance: number;
    legal: number;
    management: number;
    mortgageInterest: number;
    repairs: number;
    supplies: number;
    taxes: number;
    utilities: number;
    depreciation: number;
    other: number;
    totalExpenses: number;

    // Net
    netIncome: number;
  }>;

  // Report totals
  totalRentalIncome: number;
  totalExpenses: number;
  totalNetIncome: number;

  // Supporting documents
  attachments: Array<{
    id: string;
    name: string;
    url: string;
    category: string;
  }>;

  // Status
  status: 'draft' | 'finalized' | 'filed';

  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

// API Request/Response Types for Ledger
export interface CreateTransactionRequest {
  accountId: string;
  date: string;
  amount: number;
  description: string;
  category?: TransactionCategory;
  propertyId?: string;
  notes?: string;
}

export interface UpdateTransactionRequest {
  category?: TransactionCategory;
  subcategory?: string;
  propertyId?: string;
  reconciliationStatus?: string;
  notes?: string;
  isTaxDeductible?: boolean;
  taxCategory?: string;
}

export interface GetTransactionsRequest {
  accountId: string;
  startDate?: string;
  endDate?: string;
  propertyId?: string;
  category?: TransactionCategory;
  reconciliationStatus?: string;
  source?: string;
  page?: number;
  limit?: number;
  sortBy?: 'date' | 'amount' | 'category';
  sortOrder?: 'asc' | 'desc';
}

export interface GetLedgerSummaryRequest {
  accountId: string;
  startDate: string;
  endDate: string;
  propertyId?: string;
  includePreviousPeriod?: boolean;
}

// Firestore Collection Paths
export const FIRESTORE_COLLECTIONS = {
  ACCOUNTS: 'accounts',
  ACCOUNT_PORTFOLIOS: 'account_portfolios',
  AUTHORIZED_USERS: 'authorized_users',
  USER_ACCOUNTS: 'user_accounts',
  USERS: 'users',

  // Property notes
  PROPERTY_NOTES: 'property_notes',

  // Ledger collections
  TRANSACTIONS: 'transactions',
  CATEGORIZATION_RULES: 'categorization_rules',
  LEDGER_SUMMARIES: 'ledger_summaries',
  TAX_REPORTS: 'tax_reports',

  // Other collections
  AI_ADVICE_LOGS: 'ai_advice_logs',
  RATE_LIMITS: 'rate_limits',
  FUNCTION_EXECUTIONS: 'function_executions',
} as const;

export * from './property-report';
